/**
 * Derived string lines (sharp-labels WP-C).
 *
 * The module grid's numbering IS the electrical run: a polyline through the
 * module centers in moduleNumber order (1→N) reproduces the string's path —
 * including the U-turn where a string returns along a second row, which
 * emerges from the ordering with zero extra logic. Derived, not traced from
 * the PDF, so it re-derives when the grid changes and can carry direction
 * and polarity the sheets rarely show (owner: 1 = plus end, N = minus end).
 *
 * Input is the rendered grid FeatureCollection (generateModuleGridForLayout
 * output / gridGeojsonCache), which already reflects stringOverrides'
 * modulePositions — a moved module bends its string's line automatically.
 *
 * CONTRACT: every feature carries a top-level `id` — the layer sources use
 * `dynamic: true`, and dynamic geojson sources SILENTLY DROP id-less
 * features (hard-won 2026-08-25 lesson).
 */

import { numericFeatureId } from './featureIds';

type ModulePoint = { moduleNumber: number; center: [number, number] };

/** Color GROUP key: the string's parent in the naming hierarchy — strip the
 *  LAST segment so every sibling string shares its parent's hue and parent
 *  areas read visually (owner design, 2026-08-26). Separator-agnostic:
 *  "S2.7.4A" → "S2.7" (dots, Kfar), "ST1-DCCB10-STR3" → "ST1-DCCB10"
 *  (dashes, Hatzav — the site that proved dots-only was wrong), underscores
 *  likewise. Labels without separators group as themselves. */
export function stringColorGroup(label: string): string {
  // Parenthetical metadata first: Kalia's "2.INV01.STR01(1.PV2)" carries a
  // dot INSIDE the suffix, which made the last-separator rule split on it —
  // every string became its own color group and the per-inverter hue
  // grouping dissolved into a rainbow (owner-reported).
  const core = label.replace(/\s*\([^)]*\)\s*$/, '');
  const idx = Math.max(
    core.lastIndexOf('.'),
    core.lastIndexOf('-'),
    core.lastIndexOf('_'),
  );
  return idx > 0 ? core.slice(0, idx) : core;
}

/** Deterministic hue per color group — 12 steps around the wheel. */
export function stringLineColor(groupKey: string): string {
  let h = 0;
  for (let i = 0; i < groupKey.length; i++) {
    h = (h * 31 + groupKey.charCodeAt(i)) >>> 0;
  }
  const hue = (h % 12) * 30;
  return `hsl(${hue}, 70%, 60%)`;
}

function ringCenter(ring: Array<[number, number]>): [number, number] {
  // Average the ring's distinct corners (last coord repeats the first).
  const n = ring.length - 1;
  let lng = 0;
  let lat = 0;
  for (let i = 0; i < n; i++) {
    lng += ring[i][0];
    lat += ring[i][1];
  }
  return [lng / n, lat / n];
}

/**
 * Derive per-string LineStrings (+ polarity endpoint markers) from the grid.
 * Strings with fewer than 2 modules yield no line.
 *
 * `labelTextById` maps a string entity id to its LABEL TEXT ("S2.7.4A") so
 * colors can group by the naming hierarchy's parent; without it (or for ids
 * missing from the map) the entity id itself is the group.
 */
export function deriveStringLines(
  grid: GeoJSON.FeatureCollection,
  labelTextById?: Map<string, string>,
): GeoJSON.FeatureCollection {
  const byString = new Map<string, ModulePoint[]>();
  for (const f of grid.features) {
    const p = f.properties as { entityId?: string; moduleNumber?: number | string } | null;
    // Tolerant on purpose: moduleNumber has been seen as a numeric STRING in
    // real layout data, and a strict typeof check silently dropped those
    // strings — the owner's smoke showed exactly that as lines with holes
    // and lineless strings.
    const moduleNumber = p?.moduleNumber === undefined || p?.moduleNumber === null
      ? NaN
      : Number(p.moduleNumber);
    if (!p?.entityId || !Number.isFinite(moduleNumber)) continue;
    if (f.geometry.type !== 'Polygon') continue;
    const ring = (f.geometry as GeoJSON.Polygon).coordinates[0] as Array<[number, number]>;
    if (!ring || ring.length < 4) continue;
    let arr = byString.get(p.entityId);
    if (!arr) { arr = []; byString.set(p.entityId, arr); }
    arr.push({ moduleNumber, center: ringCenter(ring) });
  }

  const features: GeoJSON.Feature[] = [];
  for (const [entityId, mods] of byString) {
    if (mods.length < 2) continue;
    mods.sort((a, b) => a.moduleNumber - b.moduleNumber);
    const coords = mods.map((m) => m.center);
    const lineColor = stringLineColor(stringColorGroup(labelTextById?.get(entityId) ?? entityId));
    // Ids are NUMERIC (hashed): string feature ids made the dynamic RT tiler
    // drop features at scale (see numericFeatureId).
    features.push({
      type: 'Feature',
      id: numericFeatureId(`line-${entityId}`),
      properties: { entityId, kind: 'line', lineColor },
      geometry: { type: 'LineString', coordinates: coords },
    });
    features.push({
      type: 'Feature',
      id: numericFeatureId(`plus-${entityId}`),
      properties: { entityId, kind: 'polarity', sign: '+', lineColor },
      geometry: { type: 'Point', coordinates: coords[0] },
    });
    features.push({
      type: 'Feature',
      id: numericFeatureId(`minus-${entityId}`),
      properties: { entityId, kind: 'polarity', sign: '−', lineColor },
      geometry: { type: 'Point', coordinates: coords[coords.length - 1] },
    });
  }
  return { type: 'FeatureCollection', features };
}
